import { Box, Card, Tab, Tabs, Typography } from '@mui/material';
import Page from 'components/util/Page';
import Organizations from './components/Organizations';
import { useNavigate, useParams } from 'react-router';
import { PATH_DASHBOARD } from 'routes/paths';
import { Button } from '@nps.today/component-library';
import { useEffect, useState } from 'react';
import AddOrganizationDialog from './components/AddOrganizationDialog';
import Icon from 'components/icons/Icon';
import AdminLicense from './components/AdminLicense';
import ToggleEdit from 'components/page/detailsPage/components/ToggleEdit';
import { useTranslator } from 'translation/useTranslator';
import { AdminFeatureFlags } from './components/featureFlags/AdminFeatureFlags';
import { AdminAiFeedbackConfiguration } from './components/AdminAiFeedbackConfiguration';
import AdminOrderTypes from './components/orderTypes/AdminOrderTypes';
import AccessGuard, { rules, useAccess } from 'access';

export enum AdminTab {
  ORGANIZATIONS = 'organizations',
  LICENSE = 'license',
  AI_CONFIGURATION = 'ai-configuration',
  FEATURE_FLAGS = 'feature-flags',
  ORDER_TYPES = 'order-types'
}

const Admin = () => {
  const translator = useTranslator();
  const { tab = AdminTab.ORGANIZATIONS } = useParams();
  const navigate = useNavigate();
  const [displayAddOrg, setDisplayAddOrg] = useState(false);
  const [displayAddLinkedOrg, setDisplayAddLinkedOrg] = useState(false);
  const [displayAddOrderType, setDisplayAddOrderType] = useState(false);
  const [licenseEditable, setLicenseEditable] = useState(false);
  const canReadChildTenants = useAccess(rules.organization.manage);

  const canViewLicense = useAccess(rules.childTenant.read);
  const canViewAiConfiguration = useAccess(rules.aiSupport.config);
  const canViewFeatureFlags = useAccess(rules.featureFlag.manage);
  const canManageOrderTypes = useAccess(rules.orderType.manage);

  const TABS = [
    ...(canReadChildTenants
      ? [
          {
            value: AdminTab.ORGANIZATIONS,
            label: translator.organizations(),
            icon: <Icon type="organizations" width={20} height={20} />,
            component: <Organizations />,
            rule: rules.organization.manage
          }
        ]
      : []),
    ...(canViewLicense
      ? [
          {
            value: AdminTab.LICENSE,
            label: translator.license(),
            icon: <Icon type="license" width={20} height={20} />,
            component: (
              <AdminLicense editable={licenseEditable} onSave={() => setLicenseEditable(false)} />
            ),
            rule: rules.childTenant.read
          }
        ]
      : []),
    ...(canViewAiConfiguration
      ? [
          {
            value: AdminTab.AI_CONFIGURATION,
            label: translator.aiFeedback(),
            icon: <Icon type="ai" width={20} height={20} />,
            component: <AdminAiFeedbackConfiguration />,
            rule: rules.aiSupport.config
          }
        ]
      : []),
    ...(canViewFeatureFlags
      ? [
          {
            value: AdminTab.FEATURE_FLAGS,
            label: translator.featureFlags(),
            icon: <Icon type="flag" width={20} height={20} />,
            component: <AdminFeatureFlags />,
            rule: rules.featureFlag.manage
          }
        ]
      : []),
    ...(canManageOrderTypes
      ? [
          {
            value: AdminTab.ORDER_TYPES,
            label: translator.orderTypes(),
            icon: <Icon type="orderandusage" width={20} height={20} />,
            component: (
              <AdminOrderTypes
                displayAddDrawer={displayAddOrderType}
                onCloseAddDrawer={() => setDisplayAddOrderType(false)}
              />
            ),
            rule: rules.orderType.manage
          }
        ]
      : [])
  ];

  const activeTab = TABS.some((tabItem) => tabItem.value === tab) ? tab : TABS[0]?.value;

  useEffect(() => {
    if (activeTab && activeTab !== tab) {
      navigate(`${PATH_DASHBOARD.admin.root}/${activeTab}`, { replace: true });
    }
  }, [activeTab, tab, navigate]);

  const onTabChange = (newTab: AdminTab) => {
    navigate(`${PATH_DASHBOARD.admin.root}/${newTab}`);
  };

  return (
    <>
      <Page
        title={translator.admin()}
        stickyHeaderContent={[
          [
            activeTab === AdminTab.ORGANIZATIONS && (
              <AccessGuard rule={rules.tenant.create} key="addOrganization">
                <Button
                  dataTestId="add-organization"
                  variant={'contained'}
                  onClick={() => setDisplayAddOrg(true)}
                >
                  {translator.addOrganization()}
                </Button>
              </AccessGuard>
            ),
            activeTab === AdminTab.LICENSE && (
              <ToggleEdit
                isEditable={licenseEditable}
                onToggleEditable={() => setLicenseEditable((prev) => !prev)}
              />
            ),
            activeTab === AdminTab.ORDER_TYPES && (
              <AccessGuard rule={rules.orderType.manage} key="addOrderType">
                <Button
                  dataTestId="add-order-type"
                  variant={'contained'}
                  onClick={() => setDisplayAddOrderType(true)}
                >
                  {translator.addOrderType()}
                </Button>
              </AccessGuard>
            )
          ]
        ]}
      >
        <Typography>{translator.adminPageDescription()}</Typography>

        <Box sx={{ p: 1 }} />

        <Card sx={{ p: 3 }}>
          <div style={{ display: 'inline-grid' }}>
            <Tabs
              value={activeTab}
              scrollButtons="auto"
              variant="scrollable"
              allowScrollButtonsMobile
              onChange={(_, value) => onTabChange(value as AdminTab)}
            >
              {TABS.map((tabItem) => (
                <Tab
                  disableRipple
                  key={tabItem.value}
                  label={tabItem.label}
                  icon={tabItem.icon}
                  value={tabItem.value}
                />
              ))}
            </Tabs>
          </div>

          <Box sx={{ mb: 1 }} />

          {TABS.map((tabItem) => {
            const isMatched = tabItem.value === activeTab;
            return (
              isMatched && (
                <AccessGuard key={tabItem.value} rule={tabItem.rule} displayNoAccess={true}>
                  <Box>{tabItem.component}</Box>
                </AccessGuard>
              )
            );
          })}
        </Card>
      </Page>
      <AddOrganizationDialog
        display={displayAddOrg || displayAddLinkedOrg}
        close={() => {
          setDisplayAddOrg(false);
          setDisplayAddLinkedOrg(false);
        }}
        isLinkedOrg={displayAddLinkedOrg}
      />
    </>
  );
};

export default Admin;
